import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { InstanceConfigService } from '../../core/services/instance-config.service';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import {
  LucideAngularModule, LucideIconData,
  LayoutDashboard, Truck, Plus, Package, Menu, X, Wallet, Wrench, FilePen, Building2,
  ReceiptText, CirclePlus, ChevronRight, Moon, Sun, LogOut,
} from 'lucide-angular';
import { MenuService } from '../services/menu.service';
import { CierreSesionService } from '../services/cierre-sesion.service';
import { AuthService } from '../../features/auth/services/auth.service';
import { ThemeService } from '../../core/services/theme.service';
import { PanelResumenService } from '../../features/admin/services/panel-resumen.service';

interface NavLink {
  label: string;
  icon: LucideIconData;
  route: string;
}

@Component({
  selector: 'app-bottom-nav',
  standalone: true,
  imports: [CommonModule, RouterModule, LucideAngularModule],
  template: `
    <nav class="bottom-nav">
      <!-- Una cuenta de terreno (operador/técnico, #853) no tiene "Inicio" de
           gestión ni tab de finanzas —su Inicio es su propio taller u
           horómetro—, así que el primer tab lleva ahí y el segundo no se
           muestra: mostrarlo era invitar a tocar algo que igual rebota. -->
      @if (esCuentaDeTerreno) {
        <a class="bnav" [routerLink]="authService.getHomeRoute()" routerLinkActive="active">
          <lucide-angular [img]="icons.Wrench" [size]="20"></lucide-angular>
          <span>Taller</span>
        </a>
      } @else if (instanceConfig.esPanelPlataforma()) {
        <!-- El panel de plataforma (mockup 29-oct): Clientes, Cobranza y Alta a
             mano; Planes, Cotizador, tema y salir en «Más». -->
        <a class="bnav" routerLink="/admin/plataforma" routerLinkActive="active">
          <lucide-angular [img]="icons.Building2" [size]="20"></lucide-angular>
          <span>Clientes</span>
        </a>
        <a class="bnav" routerLink="/admin/pagos-suscripcion" routerLinkActive="active"
           [attr.aria-label]="atrasados() ? 'Cobranza, ' + atrasados() + (atrasados() === 1 ? ' atrasado' : ' atrasados') : null">
          <span class="bnav__ic">
            <lucide-angular [img]="icons.ReceiptText" [size]="20"></lucide-angular>
            @if (atrasados()) { <span class="bnav__cnt" aria-hidden="true">{{ atrasados() }}</span> }
          </span>
          <span>Cobranza</span>
        </a>
        <a class="bnav" routerLink="/admin/alta-cliente" routerLinkActive="active">
          <lucide-angular [img]="icons.CirclePlus" [size]="20"></lucide-angular>
          <span>Alta</span>
        </a>
      } @else if (instanceConfig.soloServicioTecnico()) {
        <!-- Sin arriendo, el inicio es el taller: un «Inicio» a /dashboard rebota ahí y nunca queda marcado. -->
        <a class="bnav" routerLink="/servicio-tecnico" routerLinkActive="active">
          <lucide-angular [img]="icons.Wrench" [size]="20"></lucide-angular>
          <span>Taller</span>
        </a>
      } @else {
        <a class="bnav" routerLink="/dashboard" routerLinkActive="active"
           [routerLinkActiveOptions]="{ exact: true }">
          <lucide-angular [img]="icons.LayoutDashboard" [size]="20"></lucide-angular>
          <span>Inicio</span>
        </a>
        <!-- Modo COSTOS (sin guías): el tab de finanzas apunta a Estados de Pago -->
        @if (finanzasTab(); as tab) {
          <a class="bnav" [routerLink]="tab.route" routerLinkActive="active">
            <lucide-angular [img]="tab.icon" [size]="20"></lucide-angular>
            <span>{{ tab.label }}</span>
          </a>
        }
      }

      <!-- CTA central · creación rápida. Solo si hay algo que crear: la cuenta del
           taller no tiene ninguna acción de gestión, y un «+» que abre una hoja
           vacía parece un botón roto. -->
      @if (quickCreate().length) {
        <button type="button" class="bnav bnav--cta" (click)="toggleCreate()"
                [class.active]="createOpen()" aria-label="Crear">
          <lucide-angular [img]="icons.Plus" [size]="22"></lucide-angular>
        </button>
      }

      <!-- La flota propia es del arriendo. Sin ella, el lugar lo toman las cotizaciones, que es lo que administración
           hace en una cuenta de taller. -->
      @if (instanceConfig.esPanelPlataforma()) {
        <!-- Nada del arriendo en el panel. -->
      } @else if (instanceConfig.muestraArriendo()) {
        <a class="bnav" routerLink="/equipos" routerLinkActive="active">
          <lucide-angular [img]="icons.Package" [size]="20"></lucide-angular>
          <span>Equipos</span>
        </a>
      } @else if (authService.isAdmin()) {
        <a class="bnav" routerLink="/cotizaciones" routerLinkActive="active">
          <lucide-angular [img]="icons.FilePen" [size]="20"></lucide-angular>
          <span>Cotizaciones</span>
        </a>
      }
      <button type="button" class="bnav" (click)="toggleMore()"
              [class.active]="moreOpen() || enMas()" aria-label="Más opciones">
        <lucide-angular [img]="icons.Menu" [size]="20"></lucide-angular>
        <span>Más</span>
      </button>
    </nav>

    <!-- Hoja · creación rápida (grid 2 columnas) -->
    @if (createOpen() && quickCreate().length) {
      <div class="sheet-overlay" (click)="createOpen.set(false)"></div>
      <div class="sheet">
        <div class="sheet__header">
          <span>Crear</span>
          <button class="sheet__close" (click)="createOpen.set(false)" aria-label="Cerrar">
            <lucide-angular [img]="icons.X" [size]="18"></lucide-angular>
          </button>
        </div>
        <div class="sheet__grid">
          @for (item of quickCreate(); track item.route) {
            <a class="qc" [routerLink]="item.route" (click)="createOpen.set(false)">
              <span class="qc__ic"><lucide-angular [img]="item.icon" [size]="20"></lucide-angular></span>
              <span class="qc__label">{{ item.label }}</span>
            </a>
          }
        </div>
      </div>
    }

    <!-- Drawer · Más (todas las rutas) -->
    @if (moreOpen()) {
      <div class="sheet-overlay" (click)="moreOpen.set(false)"></div>
      <div class="sheet sheet--menu">
        <div class="sheet__header">
          <span>Menú</span>
          <button class="sheet__close" (click)="moreOpen.set(false)" aria-label="Cerrar">
            <lucide-angular [img]="icons.X" [size]="18"></lucide-angular>
          </button>
        </div>
        @if (instanceConfig.esPanelPlataforma()) {
          @for (item of comercialPanel(); track item.route) {
            <a class="sheet__item" [routerLink]="item.route"
               routerLinkActive="active" (click)="moreOpen.set(false)">
              <span class="sheet__ic"><lucide-angular [img]="item.icon" [size]="18"></lucide-angular></span>
              <span class="sheet__lbl">{{ item.label }}</span>
              <lucide-angular class="sheet__chev" [img]="icons.ChevronRight" [size]="16"></lucide-angular>
            </a>
          }
          <button type="button" class="sheet__item" (click)="alternarTema()">
            <span class="sheet__ic"><lucide-angular [img]="oscuro() ? icons.Sun : icons.Moon" [size]="18"></lucide-angular></span>
            <span class="sheet__lbl">{{ oscuro() ? 'Tema claro' : 'Tema Onyx' }}</span>
          </button>
          <button type="button" class="sheet__item" (click)="salir()">
            <span class="sheet__ic"><lucide-angular [img]="icons.LogOut" [size]="18"></lucide-angular></span>
            <span class="sheet__lbl">Cerrar sesión</span>
          </button>
        } @else {
          @for (item of moreMenuItems(); track item.route) {
            <a class="sheet__item" [routerLink]="item.route"
               routerLinkActive="active" (click)="moreOpen.set(false)">
              <span class="sheet__ic"><lucide-angular [img]="item.icon" [size]="18"></lucide-angular></span>
              <span>{{ item.label }}</span>
            </a>
          }
        }
      </div>
    }
  `,
  styles: [`
    .bottom-nav {
      display: flex;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      height: 60px;
      background: var(--card);
      border-top: 1px solid var(--hairline);
      z-index: var(--z-overlay);
      justify-content: space-around;
      align-items: center;
      padding-bottom: env(safe-area-inset-bottom);
      box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
    }

    .bnav {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.15rem;
      min-width: 56px;
      padding: 0.35rem 0.2rem;
      border: none;
      background: transparent;
      color: var(--ink-mute);
      font-size: var(--fs-2xs);
      font-weight: var(--fw-medium);
      text-decoration: none;
      cursor: pointer;
      transition: color var(--transition-fast) ease;

      &.active { color: var(--accent-text); }
    }

    /* CTA central · botón de acento elevado */
    .bnav--cta {
      flex: 0 0 auto;
      width: 46px;
      height: 46px;
      min-width: 46px;
      border-radius: 50%;
      background: var(--accent);
      color: #fff;
      transform: translateY(-8px);
      box-shadow: var(--shadow-md);
      transition: background var(--transition-fast) ease, transform var(--transition-fast) ease;

      &:hover { background: var(--accent-hover, var(--accent)); }
      &:active { transform: translateY(-8px) scale(0.94); }
      &.active { color: #fff; }
    }

    /* Overlay + hojas inferiores (creación rápida y menú "Más") */
    .sheet-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.4);
      z-index: var(--z-modal-backdrop);
      animation: fadeIn 0.15s ease;
    }

    .sheet {
      position: fixed;
      bottom: 60px;
      left: 0;
      right: 0;
      background: var(--card);
      border-top: 1px solid var(--hairline);
      border-radius: 16px 16px 0 0;
      z-index: var(--z-modal);
      padding: 0.5rem;
      padding-bottom: calc(0.5rem + env(safe-area-inset-bottom));
      max-height: 60vh;
      overflow-y: auto;
      animation: slideUp 0.2s ease;
      box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.12);
    }

    .sheet__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.5rem 0.75rem;
      font-weight: var(--fw-semibold);
      font-size: var(--fs-lg);
      color: var(--ink);
      border-bottom: 1px solid var(--hairline);
      margin-bottom: 0.25rem;
    }

    .sheet__close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      background: none;
      border: none;
      color: var(--ink-mute);
      cursor: pointer;
      border-radius: 50%;

      &:hover { background: var(--paper-tint); color: var(--ink); }
    }

    /* Creación rápida · grid 2 columnas con tarjetas grandes */
    .sheet__grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.5rem;
      padding: 0.25rem;
    }

    .qc {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
      padding: 1rem 0.75rem;
      border: 1px solid var(--hairline);
      border-radius: var(--radius-md);
      background: var(--paper-tint);
      color: var(--ink);
      text-decoration: none;
      text-align: center;
      transition: border-color var(--transition-fast) ease, background var(--transition-fast) ease;

      &:hover { border-color: var(--accent); background: color-mix(in srgb, var(--accent) 6%, var(--card)); }
    }

    .qc__ic {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: var(--radius-md);
      background: color-mix(in srgb, var(--accent) 12%, transparent);
      color: var(--accent);
    }

    .qc__label { font-size: var(--fs-sm); font-weight: var(--fw-medium); }

    /* Drawer "Más" · lista vertical */
    .sheet__item {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.625rem 0.75rem;
      color: var(--ink);
      text-decoration: none;
      border-radius: var(--radius-md);
      font-size: var(--fs-base);
      transition: background var(--transition-fast) ease;

      &:hover { background: var(--paper-tint); }

      &.active {
        background: color-mix(in srgb, var(--accent) 8%, transparent);
        color: var(--accent-text);
      }
    }

    .sheet__ic {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      color: var(--ink-mute);
    }

    .sheet__item.active .sheet__ic { color: var(--accent); }

    /* La regla global de enlaces (a:not(.p-button):not(…) → acento, especificidad 0,4,1) le ganaba a .bnav y a
       .sheet__item: todas las pestañas y todas las filas de «Más» salían en naranja, no solo la activa. */
    .bottom-nav a.bnav:not(.active) { color: var(--ink-mute); }
    .sheet a.sheet__item:not(.active) { color: var(--ink); }

    /* Las entradas del «Más» del panel que no navegan (tema, salir) son botones: mismo aspecto que los enlaces. */
    button.sheet__item {
      width: 100%;
      border: none;
      background: none;
      font: inherit;
      font-size: var(--fs-base);
      text-align: left;
      cursor: pointer;
    }
    .sheet__item { min-height: 44px; }
    .sheet__lbl { flex: 1; }
    .sheet__chev { display: inline-flex; color: var(--ink-faint); }

    /* Contador sobre el ícono (cobros atrasados del panel). Rojo: es plata que no llegó. */
    .bnav__ic { position: relative; display: inline-flex; }
    .bnav__cnt {
      position: absolute;
      top: -6px;
      left: calc(100% - 6px);
      min-width: 18px;
      height: 18px;
      padding: 0 4px;
      border-radius: var(--radius-pill);
      border: 2px solid var(--card);
      background: var(--danger);
      color: var(--btn-danger-fg);
      font-family: var(--font-mono);
      font-size: var(--fs-2xs);
      font-weight: var(--fw-bold);
      line-height: 14px;
      text-align: center;
      font-variant-numeric: tabular-nums;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes slideUp {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
    }
  `]
})
export class BottomNavComponent {
  private menuService = inject(MenuService);
  protected instanceConfig = inject(InstanceConfigService);
  protected authService = inject(AuthService);

  readonly icons = {
    LayoutDashboard, Truck, Plus, Package, Menu, X, Wrench, FilePen, Building2,
    ReceiptText, CirclePlus, ChevronRight, Moon, Sun, LogOut,
  };

  private router = inject(Router);
  private themeService = inject(ThemeService);
  private cierreSesion = inject(CierreSesionService);
  private panelResumen = inject(PanelResumenService);

  /** Cuotas del mes vencidas sin pagar: el contador de «Cobranza» del panel, el mismo del menú lateral. */
  readonly atrasados = this.panelResumen.cobrosAtrasados;
  readonly comercialPanel = computed(() => this.menuService.seccionComercialDelPanel().items);
  readonly oscuro = toSignal(this.themeService.isDarkMode(), { initialValue: false });

  private readonly url = toSignal(
    this.router.events.pipe(filter(e => e instanceof NavigationEnd), map(() => this.router.url)),
    { initialValue: this.router.url });
  /** En el panel, Planes y Cotizador viven en «Más»: estando ahí, «Más» se marca. */
  readonly enMas = computed(() => this.instanceConfig.esPanelPlataforma()
    && this.comercialPanel().some(i => !!i.route && this.url().startsWith(i.route)));

  /** Operador o técnico (#853): sin "Inicio" de gestión ni tab de finanzas. */
  readonly esCuentaDeTerreno = this.authService.isFieldAccount();

  createOpen = signal(false);
  moreOpen = signal(false);

  // Reactivos al modo de instancia: se re-filtran cuando llega /config/instancia.
  readonly moreMenuItems = computed<NavLink[]>(() => this.menuService.buildMobileMenuItemsV16());
  readonly quickCreate = computed<NavLink[]>(() => this.menuService.buildQuickCreateItems());

  /**
   * Tab fijo de finanzas: Guías cuando la instancia las usa · Estados de Pago
   * en COSTOS. El criterio es si USA guías, no si emite DTE por ellas: un
   * cliente que factura en otro sistema despacha igual.
   */
  readonly finanzasTab = computed(() => {
    // Guías y estados de pago son del arriendo.
    if (!this.instanceConfig.muestraArriendo()) return null;
    if (this.instanceConfig.requiereGuiaParaActivar()) {
      return { route: '/facturacion/guias', label: 'Guías', icon: Truck };
    }
    // Sin guías ni estados de pago no hay tab de finanzas: uno que lleva a una
    // pantalla apagada rebota al inicio y parece roto.
    return this.instanceConfig.muestraEstadosPago()
      ? { route: '/facturacion/estados-pago', label: 'E. de Pago', icon: Wallet }
      : null;
  });

  toggleCreate() {
    this.moreOpen.set(false);
    this.createOpen.update(v => !v);
  }

  toggleMore() {
    this.createOpen.set(false);
    this.moreOpen.update(v => !v);
  }

  alternarTema(): void {
    this.themeService.toggleDarkMode();
    this.moreOpen.set(false);
  }

  salir(): void {
    this.moreOpen.set(false);
    this.cierreSesion.pedir();
  }
}
